import { useState, useEffect } from 'react';
import { apiFetch, ApiError } from '../lib/api';

// Sprint UXV UXV.F4 (#1420) — per-project Claude Design export override card.
//
// Mirrors ProjectBrandingOverrideSidebarCard / EscalationOverridesSidebarCard:
// collapsible, CSS-var inline styles. A TRI-STATE control lets a TENANT_ADMIN /
// project TECH_LEAD / PRODUCT_MANAGER override the tenant-level Claude Design
// export default for THIS project:
//   Inherit  → store NULL  (re-inherit the tenant default)
//   Enabled  → store TRUE
//   Disabled → store FALSE
// The "Tenant default: On/Off" hint shows what Inherit resolves to (cf. the
// branding card's inherited-value reference). Instant-save on select (no Save
// button), matching the tenant toggle + estimation radios.
//
// Data comes via props — `override` from the GET /projects/:id the dashboard
// already loaded, `tenantDefault` from useMyTenant — so the card adds no new
// round-trip (UXV.F4 §6.7).
//
// Access is SERVER-authoritative: PATCH /projects/:id/settings role-gates the
// write (TENANT_ADMIN or project TECH_LEAD/PM). A caller without the role gets
// a 403, which this card surfaces inline and reverts — so the control is shown
// to every viewer but only the permitted roles can persist a change.

type Choice = 'inherit' | 'enabled' | 'disabled';

function overrideToChoice(override: boolean | null): Choice {
  if (override === true) return 'enabled';
  if (override === false) return 'disabled';
  return 'inherit';
}
function choiceToOverride(choice: Choice): boolean | null {
  if (choice === 'enabled') return true;
  if (choice === 'disabled') return false;
  return null;
}

interface Props {
  projectId: string;
  /** Current per-project override; null = inherit the tenant default. */
  override: boolean | null;
  /** Tenant-level default (from useMyTenant). */
  tenantDefault: boolean;
  /** Called with the freshly-saved override so the parent can refresh state. */
  onChange?: (next: boolean | null) => void;
}

const cardCls = 'rounded-xl border shadow-sm transition-shadow duration-150';
const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', borderColor: 'var(--border)', padding: 'var(--space-5)',
};

const OPTIONS: Array<{ value: Choice; label: string }> = [
  { value: 'inherit',  label: 'Inherit' },
  { value: 'enabled',  label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

export default function ProjectClaudeDesignOverrideSidebarCard({ projectId, override, tenantDefault, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<Choice>(overrideToChoice(override));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // Keep the local selection in sync if the parent's override changes
  // (another save, or a project switch).
  useEffect(() => {
    setChoice(overrideToChoice(override));
    setError('');
    setSaved(false);
  }, [override]);

  const select = async (next: Choice) => {
    if (next === choice || saving) return;
    const prev = choice;
    setChoice(next);
    setError('');
    setSaved(false);
    setSaving(true);
    const nextOverride = choiceToOverride(next);
    try {
      await apiFetch(`/projects/${projectId}/settings`, {
        method: 'PATCH',
        body: JSON.stringify({ claudeDesignExportEnabled: nextOverride }),
      });
      onChange?.(nextOverride);
      setSaved(true);
    } catch (e) {
      setChoice(prev); // revert to last-known-good
      if (e instanceof ApiError && e.status === 403) {
        setError('Only a Tech Lead, PM, or Admin can change this.');
      } else {
        setError(e instanceof Error ? e.message : 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  // Effective value for the "currently shown/hidden" hint (mirrors the
  // server resolver: override wins iff non-null, else the tenant default).
  const effective = override === null ? tenantDefault : override;

  return (
    <div className={cardCls} style={cardStyle}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="project-claude-design-toggle"
        aria-expanded={open}
        style={{
          width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <p
          className="uppercase tracking-wider"
          style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
            margin: 0, letterSpacing: '0.05em',
            borderLeft: '3px solid var(--primary)', paddingLeft: 8,
          }}
        >
          Claude Design Export{override !== null ? ' (override tenant)' : ''}
        </p>
        <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 10px' }}>
            Controls whether the “Export to Claude Design” action appears on this project’s Phase 2.5 artefacts.
          </p>

          <div role="radiogroup" aria-label="Claude Design export" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {OPTIONS.map(opt => {
              const selected = choice === opt.value;
              const hint = opt.value === 'inherit'
                ? ` (Tenant default: ${tenantDefault ? 'On' : 'Off'})`
                : '';
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-testid={`project-claude-design-option-${opt.value}`}
                  disabled={saving}
                  onClick={() => void select(opt.value)}
                  style={{
                    textAlign: 'left', padding: '8px 10px', borderRadius: 6,
                    border: selected ? '2px solid var(--primary)' : '1px solid var(--border)',
                    background: selected ? 'rgba(124,58,237,0.06)' : 'var(--surface)',
                    color: 'var(--text)', fontSize: 13, cursor: saving ? 'default' : 'pointer',
                  }}
                >
                  {opt.label}
                  {hint && <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{hint}</span>}
                </button>
              );
            })}
          </div>

          <p data-testid="project-claude-design-effective" style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '8px 0 0' }}>
            Export action is currently {effective ? 'shown' : 'hidden'} for this project.
          </p>

          {error && (
            <div data-testid="project-claude-design-error" style={{
              padding: '6px 10px', background: 'rgba(239,68,68,0.08)', color: 'var(--error)',
              borderRadius: 6, marginTop: 8, fontSize: 12,
            }}>{error}</div>
          )}
          {saved && !error && (
            <div data-testid="project-claude-design-saved" style={{
              padding: '6px 10px', background: 'rgba(16,185,129,0.08)', color: '#10b981',
              borderRadius: 6, marginTop: 8, fontSize: 12,
            }}>Saved</div>
          )}
        </div>
      )}
    </div>
  );
}
